/**
 * Generic headless data table (TanStack Table) with optional row navigation,
 * server-driven sorting and either cursor ("load more") or page-number
 * pagination.
 *
 * Sorting is *manual*: columns with `enableSorting: true` render a clickable
 * header and the table reports the ordering string (`"-due_at"`) through
 * `onSortingChange`; the page re-queries the API. Client-side sorting would
 * only reorder the rows already loaded, which misleads on paged data.
 *
 * Past `VIRTUALIZE_FROM` rows (cursor mode after many "load more" clicks) the
 * body is windowed with TanStack Virtual inside a fixed-height scroll container.
 */
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Row,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { type ReactElement, useRef } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/format";
import { Select } from "@/shared/ui/form";
import { Td, Th, Tr } from "@/shared/ui/primitives";

/** Row count from which the table body is virtualised. */
export const VIRTUALIZE_FROM = 200;
/** Estimated row height in px (dense rows: py-1.5 + text-xs). */
const ROW_HEIGHT = 33;
/** Height of the scroll container when virtualised. */
const VIEWPORT_HEIGHT = 600;

/** Page-number pagination controls (server-side). */
export interface TablePagination {
  page: number;
  pages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  sizes?: readonly number[];
  extendedNavigation?: boolean;
}

/** Props for `DataTable`. */
export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  /** Build the route for a row click; omit for non-navigable tables. */
  rowHref?: (row: T) => string;
  /** Stable row id. */
  getRowId?: (row: T) => string;
  emptyMessage?: string;
  /** Pagination hooks for cursor-based lists. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  total?: number | null;
  /** Current server ordering (`"-due_at"`); enables header sorting when provided with `onSortingChange`. */
  sorting?: string;
  onSortingChange?: (ordering: string | undefined) => void;
  /** Page-number pagination; mutually exclusive with `hasMore`/`onLoadMore`. */
  pagination?: TablePagination;
  /** Dim the body while a new page/sort is loading. */
  isFetching?: boolean;
  /** Keep dense tables readable inside their local horizontal scroll region. */
  minWidth?: number;
}

/** Parse all server ordering terms into TanStack sorting state. */
export function parseSorting(ordering: string | undefined): SortingState {
  return (ordering ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => ({
      id: entry.startsWith("-") ? entry.slice(1) : entry,
      desc: entry.startsWith("-"),
    }));
}

/** Serialise TanStack sorting state back into an ordering string. */
export function toOrdering(sorting: SortingState): string | undefined {
  return sorting.map((entry) => `${entry.desc ? "-" : ""}${entry.id}`).join(",") || undefined;
}

/** Render a dense table; rows become keyboard-navigable links when `rowHref` is set. */
export function DataTable<T>({
  columns,
  data,
  rowHref,
  getRowId,
  emptyMessage,
  hasMore,
  onLoadMore,
  isLoadingMore,
  total,
  sorting,
  onSortingChange,
  pagination,
  isFetching,
  minWidth,
}: DataTableProps<T>): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sortable = onSortingChange !== undefined;
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId ? (row): string => getRowId(row) : undefined,
    manualSorting: true,
    state: { sorting: parseSorting(sorting) },
  });
  const current = parseSorting(sorting)[0];
  /** asc → desc → cleared (back to the page's default ordering). */
  const toggleSort = (id: string): void => {
    if (current?.id !== id) {
      onSortingChange?.(id);
    } else if (!current.desc) {
      onSortingChange?.(`-${id}`);
    } else {
      onSortingChange?.(undefined);
    }
  };

  const rows = table.getRowModel().rows;
  const virtual = rows.length >= VIRTUALIZE_FROM;
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  const renderRow = (row: Row<T>, virtualIndex?: number): ReactElement => {
    const href = rowHref?.(row.original);
    return (
      <Tr
        key={row.id}
        ref={virtualIndex === undefined ? undefined : virtualizer.measureElement}
        data-index={virtualIndex}
        className={href ? "cursor-pointer" : undefined}
        tabIndex={href ? 0 : undefined}
        onClick={
          href
            ? (event): void => {
                if (
                  event.target instanceof Element &&
                  event.currentTarget.contains(event.target) &&
                  !event.target.closest("a, button, input, select, textarea, label, [role=button]")
                ) {
                  void navigate({ to: href });
                }
              }
            : undefined
        }
        onKeyDown={
          href
            ? (e): void => {
                if (e.key === "Enter" && e.target === e.currentTarget) {
                  void navigate({ to: href });
                }
              }
            : undefined
        }
      >
        {row.getVisibleCells().map((cell) => (
          <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
        ))}
      </Tr>
    );
  };

  const items = virtualizer.getVirtualItems();
  // Spacer rows keep the scrollbar proportional to the full list while only
  // the windowed rows exist in the DOM.
  const first = items.at(0);
  const last = items.at(-1);
  const padTop = first?.start ?? 0;
  const padBottom = last ? virtualizer.getTotalSize() - last.end : 0;

  return (
    <div className="space-y-2.5">
      <div
        ref={scrollRef}
        data-testid={virtual ? "virtual-table" : undefined}
        role="region"
        aria-label={t("app.table")}
        tabIndex={0}
        className={cn(
          "min-w-0 max-w-full overflow-auto rounded-md border bg-card",
          virtual && "relative",
        )}
        style={virtual ? { maxHeight: VIEWPORT_HEIGHT } : undefined}
      >
        <table
          className="w-full caption-bottom text-xs"
          style={minWidth ? { minWidth } : undefined}
        >
          <thead className="border-b">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  // Display columns have no accessor, so sortability is declared
                  // explicitly on the column def rather than via getCanSort().
                  const canSort = sortable && h.column.columnDef.enableSorting === true;
                  const dir = current?.id === h.column.id ? (current.desc ? "desc" : "asc") : false;
                  const label = h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext());
                  return (
                    <Th
                      key={h.id}
                      aria-sort={
                        dir === "asc" ? "ascending" : dir === "desc" ? "descending" : undefined
                      }
                      // Sticky lives on the cells (Safari ignores it on <thead>) and the
                      // background is opaque so scrolled rows never show through.
                      className={cn(
                        "bg-muted",
                        virtual && "sticky top-0 z-10 shadow-[0_1px_0_0_hsl(var(--border))]",
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(h.column.id)}
                          className={cn(
                            "-mx-1 inline-flex items-center gap-1 rounded px-1 hover:text-foreground",
                            dir && "text-foreground",
                          )}
                        >
                          {label}
                          {dir === "asc" ? (
                            <ArrowUp className="size-3" aria-hidden />
                          ) : dir === "desc" ? (
                            <ArrowDown className="size-3" aria-hidden />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" aria-hidden />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </Th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            className={cn(isFetching && "opacity-60 transition-opacity")}
            aria-busy={isFetching}
          >
            {rows.length === 0 ? (
              <tr>
                <td colSpan={table.getVisibleLeafColumns().length}>
                  <EmptyState message={emptyMessage} />
                </td>
              </tr>
            ) : virtual ? (
              <>
                {padTop > 0 ? (
                  <tr aria-hidden style={{ height: padTop }}>
                    <td colSpan={columns.length} />
                  </tr>
                ) : null}
                {items.map((item) => {
                  const row = rows[item.index];
                  return row ? renderRow(row, item.index) : null;
                })}
                {padBottom > 0 ? (
                  <tr aria-hidden style={{ height: padBottom }}>
                    <td colSpan={columns.length} />
                  </tr>
                ) : null}
              </>
            ) : (
              rows.map((row) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>
      {data.length === 0 && pagination && pagination.page > 1 ? (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => pagination.onPageChange(1)}>
            {t("app.firstPage")}
          </Button>
        </div>
      ) : null}
      {pagination ? (
        <Pager {...pagination} />
      ) : (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{total != null ? t("app.total", { count: total }) : null}</span>
          {hasMore && onLoadMore ? (
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
              {t("app.loadMore")}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Page-number footer: range summary, page size, previous/next. */
function Pager({
  page,
  pages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sizes = [25, 50, 100],
  extendedNavigation = false,
}: TablePagination): ReactElement {
  const { t } = useTranslation();
  const first = total === 0 || page > pages ? 0 : (page - 1) * pageSize + 1;
  const last = first === 0 ? 0 : Math.min(page * pageSize, total);
  return (
    <nav
      aria-label={t("app.pagination")}
      className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
    >
      <span>{t("app.range", { first, last, total })}</span>
      <div className="flex flex-wrap items-center gap-2">
        {onPageSizeChange ? (
          <label className="flex items-center gap-1">
            {t("app.rowsPerPage")}
            <Select
              aria-label={t("app.rowsPerPage")}
              className="h-7 w-20 py-0 text-xs"
              value={String(pageSize)}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {sizes.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        <span className="tabular-nums">{t("app.pageOf", { page, pages })}</span>
        {extendedNavigation ? (
          <Button
            variant="outline"
            size="sm"
            aria-label={t("tablePilot.firstPage")}
            title={t("tablePilot.firstPage")}
            disabled={page <= 1}
            onClick={() => onPageChange(1)}
          >
            <ChevronsLeft className="size-4 rtl:rotate-180" aria-hidden />
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          aria-label={t("app.previousPage")}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4 rtl:rotate-180" aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("app.nextPage")}
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="size-4 rtl:rotate-180" aria-hidden />
        </Button>
        {extendedNavigation ? (
          <Button
            variant="outline"
            size="sm"
            aria-label={t("tablePilot.lastPage")}
            title={t("tablePilot.lastPage")}
            disabled={page >= pages}
            onClick={() => onPageChange(pages)}
          >
            <ChevronsRight className="size-4 rtl:rotate-180" aria-hidden />
          </Button>
        ) : null}
      </div>
    </nav>
  );
}
